import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  Link,
  Separator,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FaCalendar, FaDownload, FaUser } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useGetNewsMediaPublicationQuery } from "../../features/cmsApi";
import BreadcrumbComponent from "./BreadcrumbComponent";
import { cmsBaseUrl } from "../../constants/baseUrl";
import PdfDialog from "./TheClub/PdfDialog";

interface Publication {
  id: number;
  title: string;
  details?: string;
  publicationType?: string;
  author?: string | null;
  publishedDate: string;
  category?: string | null;
  image?: string;
  attachment?: string;
  link?: string | null;
  featured?: boolean;
  thumbnail?: {
    url: string;
  };
  file?: {
    url: string;
  };
}

const Publication = () => {
  const { data } = useGetNewsMediaPublicationQuery();
  const [publications, setPublications] = useState<Publication[]>([]);
  const navigate = useNavigate();

  // Update publications state when data is received
  useEffect(() => {
    if (data?.data?.publication) {
      setPublications(data.data.publication);
    }
  }, [data]);

  const handleReadMore = (item: Publication) => {
    navigate(`/IBAXIS/${item.id}`);
  };

  const getTypeColor = (type?: string) => {
    switch (type) {
      case "Annual Report":
        return "blue";
      case "Journal":
        return "green";
      case "Newsletter":
        return "purple";
      case "Magazine":
        return "orange";
      case "Report":
        return "teal";
      default:
        return "gray";
    }
  };

  return (
    <Box px={{ base: 4, md: 6, lg: 20 }} py={{ base: 8, md: 12, lg: 20 }}>
      <Container maxW="7xl">
        <VStack gap={8} align="stretch">
          <Flex justify="space-between" align="center" w="full">
            <BreadcrumbComponent
              data={[
                { title: "Home", href: "/" },
                { title: "IBAXIS", href: null },
              ]}
            />
          </Flex>

          <Box textAlign="center" mb={{ base: 4, md: 4 }}>
            <Text
              fontSize={{ base: "2xl", sm: "3xl", md: "4xl", lg: "5xl" }}
              fontWeight="bold"
              color="#090047"
              fontFamily="Cinema Sunday"
            >
              IBAXIS
            </Text>
          </Box>
        </VStack>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 2, xl: 3 }} gap={4}>
          {publications.map((publication) => (
            <Card.Root
              key={publication.id}
              overflow="hidden"
              transition="all 0.3s"
              _hover={{
                transform: "translateY(-4px)",
                shadow: "xl",
                borderColor: "#090047",
              }}
              position="relative"
            >
              {publication.featured && (
                <Badge
                  colorScheme="red"
                  position="absolute"
                  top={3}
                  right={3}
                  zIndex={1}
                  borderRadius="full"
                  px={2}
                  py={1}
                >
                  Featured
                </Badge>
              )}

              <Card.Header p={0}>
                <Box position="relative" h="200px" overflow="hidden">
                  {publication?.thumbnail?.url ? (
                    <Avatar.Root
                      shape="square"
                      w="100%"
                      h={{ base: "180px", md: "200px", lg: "220px" }}
                      borderTopRadius="xl"
                      bg="gray.50"
                    >
                      <Avatar.Fallback
                        name={publication.title}
                        bg="gray.100"
                        color="gray.500"
                      />
                      <Avatar.Image
                        src={`${import.meta.env.VITE_CMS_API_URL}${publication?.thumbnail?.url
                          }`}
                        objectFit="contain"
                        w="100%"
                        h="100%"
                      />
                    </Avatar.Root>
                  ) : (
                    <Avatar.Root
                      shape="square"
                      w="100%"
                      h={{ base: "180px", md: "200px", lg: "220px" }}
                      bg="gray.100"
                      borderTopRadius="xl"
                    >
                      <Avatar.Fallback
                        name={publication.title}
                        bg="gray.100"
                        color="gray.400"
                        w="100%"
                        h="100%"
                      >
                        <Text fontSize="4xl">📰</Text>
                      </Avatar.Fallback>
                    </Avatar.Root>
                  )}
                </Box>
              </Card.Header>

              <Card.Body p={6}>
                <VStack align="start" gap={3}>
                  <Badge
                    colorScheme={getTypeColor(publication.publicationType)}
                    size="sm"
                    borderRadius="full"
                  >
                    {publication.publicationType ||
                      publication.category ||
                      "Publication"}
                  </Badge>

                  <Heading size="md" color="#090047" lineHeight="shorter">
                    {publication.title}
                  </Heading>

                  <Text
                    fontSize="sm"
                    color="gray.600"
                    css={{
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {publication.details
                      ? publication.details.length > 120
                        ? publication.details.slice(0, 120) + "..."
                        : publication.details
                      : "No description available"}
                  </Text>

                  <Separator />

                  <VStack gap={2} align="start" w="100%">
                    {publication.author && (
                      <HStack fontSize="sm" color="gray.500">
                        <Icon>
                          <FaUser />
                        </Icon>
                        <Text>{publication.author}</Text>
                      </HStack>
                    )}
                    <HStack fontSize="sm" color="gray.500">
                      <Icon>
                        <FaCalendar />
                      </Icon>
                      <Text>
                        {new Date(
                          publication.publishedDate
                        ).toLocaleDateString()}
                      </Text>
                    </HStack>
                  </VStack>
                </VStack>
              </Card.Body>

              <Card.Footer p={{ base: 6, sm: 4, md: 5, lg: 6 }} pt={0}>
                <Flex
                  direction={{ base: "column", sm: "row" }}
                  gap={{ base: 3, sm: 2 }}
                  w="100%"
                >
                  {publication.attachment && (
                    <Link
                      href={publication.attachment}
                      target="_blank"
                      rel="noopener noreferrer"
                      _hover={{ textDecoration: "none" }}
                      w={{ base: "100%", sm: "auto" }}
                      flex={{ base: "none", sm: 1 }}
                    >
                      <Button
                        size={{ base: "md", sm: "sm" }}
                        bg="#090047"
                        color="white"
                        _hover={{ bg: "#1a0f7a" }}
                        px={{ base: 4, sm: 2 }}
                        py={{ base: 3, sm: 1 }}
                        h={{ base: "48px", sm: "32px" }}
                        borderRadius="lg"
                        w="100%"
                      >
                        <Icon mr={2}>
                          <FaDownload />
                        </Icon>
                        Download PDF
                      </Button>
                    </Link>
                  )}
                  <Button
                    size={{ base: "md", sm: "sm" }}
                    variant="solid"
                    borderRadius={{ base: "lg", sm: "full" }}
                    bg={"#293354"}
                    _hover={{ bg: "#5E698D" }}
                    flex={{ base: "none", sm: 1 }}
                    w={{ base: "100%", sm: "auto" }}
                    px={{ base: 2, sm: 3 }}
                    py={{ base: 1, sm: 2 }}
                    h={{ base: "28px", sm: "auto" }}
                    onClick={() => handleReadMore(publication)}
                  >
                    Read More
                  </Button>
                  <Box flexShrink={0} w={{ base: "100%", sm: "auto" }}>
                    {publication.file && publication.file.url && <PdfDialog
                      title={publication.title}
                      pdfUrl={`${cmsBaseUrl}${publication.file.url}`}
                    >
                      <Button
                        size={{ base: "md", sm: "sm" }}
                        bg="blue.900"
                        color="white"
                        _hover={{ bg: "blue.700" }}
                        rounded={{ base: "lg", sm: "full" }}
                        px={{ base: 2, sm: 6 }}
                        py={{ base: 1, sm: 2 }}
                        h={{ base: "28px", sm: "auto" }}
                        w="100%"
                      >
                        {/* <Icon as={FaEye} mr={2} /> */}
                        View
                      </Button>
                    </PdfDialog>}
                  </Box>

                </Flex>
              </Card.Footer>
            </Card.Root>
          ))}
        </SimpleGrid>

        {publications.length === 0 && (
          <Box py={16} textAlign="center">
            <Text fontSize="6xl" opacity={0.3} mb={6}>
              📚
            </Text>
            <Heading size="lg" color="gray.600" mb={4}>
              No Publications Available
            </Heading>
            <Text color="gray.500">
              Check back later for the latest publications!
            </Text>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Publication;
